import type { ReactNode } from "react";

/* ---------------- FilterMenu schema (strategy §17.3, PB-2941) ----------------
   The declarative filter contract a consuming module writes ONCE per page:
   which fields exist, where their choices come from (`options`), and how a
   selection affects results (`apply`). Data-blind — no endpoints, tenants, or
   module types belong here; a module supplies loaders and predicates.

   Execution is INFERRED, never flagged: `apply.local` means "filter the
   complete in-memory dataset", `apply.requestParam` means "map the applied
   value onto the module's list request". Declaring both on one field is
   unrepresentable (`?: never` on the opposite member), so an ambiguous field
   fails to typecheck instead of silently picking a side. */

/** One selectable choice. */
export interface FilterOption {
  readonly value: string;
  readonly label: string;
  /**
   * The choice is a person (an owner filter): a remote option list draws it
   * as PeopleField's rows do — an avatar seeded by `value` (the person's id),
   * the label as the name and the email under it.
   */
  readonly person?: { readonly email?: string | null } | undefined;
}

/** A page of remote choices — cursor-paginated, `null` cursor = last page. */
export interface FilterOptionPage {
  readonly options: readonly FilterOption[];
  readonly nextCursor: string | null;
}

export interface FilterOptionsRequest {
  readonly query: string;
  readonly cursor?: string;
  readonly signal: AbortSignal;
}

/** Choices declared inline (or derived from data the page already loaded). */
export interface StaticFilterOptions {
  readonly kind: "static";
  readonly options: readonly FilterOption[];
}

/**
 * Choices fetched lazily when the menu opens. The LOADER owns caching /
 * deduplication (e.g. via the module's TanStack QueryClient) — this layer only
 * drives request timing (debounce, cancellation, pagination) and UI states.
 */
export interface RemoteFilterOptions {
  readonly kind: "remote";
  readonly load: (request: FilterOptionsRequest) => Promise<FilterOptionPage>;
  /** Show a search box over the option list (NOT the page's main search). */
  readonly searchable?: boolean;
  /** Debounce for option search (default 300ms). */
  readonly debounceMs?: number;
  readonly searchPlaceholder?: string;
  readonly emptyMessage?: string;
  /** Accept a URL-restored value; garbage is silently dropped (fail-safe). */
  readonly isValidValue?: (value: string) => boolean;
}

export type FilterOptionsSource = StaticFilterOptions | RemoteFilterOptions;

/** In-memory application over the complete supplied dataset. */
export interface FilterLocalApply<Row> {
  readonly local: (row: Row, values: readonly string[]) => boolean;
  readonly requestParam?: never;
}

/** Request mapping: the applied value(s) join the module's list request under
 * this parameter name (see {@link filterRequestParams}). */
export interface FilterRequestApply {
  readonly requestParam: string;
  readonly local?: never;
}

export type FilterApply<Row> = FilterLocalApply<Row> | FilterRequestApply;

/** Custom-control escape hatch: the field renders its own draft control. */
export interface FilterFieldRenderProps {
  readonly values: readonly string[];
  readonly onChange: (values: readonly string[]) => void;
}

export interface FilterFieldDef<Row = unknown> {
  /** Stable field id — also the URL query parameter name. */
  readonly id: string;
  readonly label: string;
  readonly type: "single" | "multi";
  readonly options: FilterOptionsSource;
  readonly apply: FilterApply<Row>;
  /** Zero-value option label for single-selects (default "All"). */
  readonly anyLabel?: string;
  readonly renderField?: (props: FilterFieldRenderProps) => ReactNode;
}

export type FilterSchema<Row = unknown> = ReadonlyArray<FilterFieldDef<Row>>;

/** Applied/draft state: field id → selected values (single = 0..1 values). */
export type FilterValues = Readonly<Record<string, readonly string[]>>;

export const EMPTY_FILTER_VALUES: FilterValues = Object.freeze({});

/**
 * Canonical form: unknown fields dropped, empties removed, values deduped,
 * multi-select values SORTED — so equal selections always serialize (and key
 * caches) identically regardless of click order.
 */
export function normalizeFilterValues<Row>(
  schema: FilterSchema<Row>,
  values: FilterValues,
): FilterValues {
  const next: Record<string, readonly string[]> = {};
  for (const field of schema) {
    const cleaned = [
      ...new Set((values[field.id] ?? []).filter((value) => value !== "")),
    ];
    if (cleaned.length === 0) continue;
    next[field.id] =
      field.type === "single" ? cleaned.slice(0, 1) : cleaned.sort();
  }
  return next;
}

/** Structural equality over NORMALIZED values (see above). */
export function isSameFilterValues(a: FilterValues, b: FilterValues): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every(
    (key) =>
      b[key] !== undefined &&
      a[key]!.length === b[key]!.length &&
      a[key]!.every((value, index) => value === b[key]![index]),
  );
}

/** Fields with ≥1 applied value — the trigger's active count. */
export function activeFilterCount<Row>(
  schema: FilterSchema<Row>,
  values: FilterValues,
): number {
  return schema.filter((field) => (values[field.id]?.length ?? 0) > 0).length;
}

/** Conjunction of every `apply.local` field with applied values. */
export function localFilterPredicate<Row>(
  schema: FilterSchema<Row>,
  values: FilterValues,
): (row: Row) => boolean {
  const active: Array<{
    readonly local: (row: Row, values: readonly string[]) => boolean;
    readonly values: readonly string[];
  }> = [];
  for (const field of schema) {
    const fieldValues = values[field.id];
    if (field.apply.local && fieldValues && fieldValues.length > 0) {
      active.push({ local: field.apply.local, values: fieldValues });
    }
  }
  return (row) => active.every((entry) => entry.local(row, entry.values));
}

/**
 * Request fragment for every `apply.requestParam` field with applied values:
 * single fields map to a string, multi fields to the (sorted) value array.
 */
export function filterRequestParams<Row>(
  schema: FilterSchema<Row>,
  values: FilterValues,
): Record<string, string | readonly string[]> {
  const params: Record<string, string | readonly string[]> = {};
  for (const field of schema) {
    const fieldValues = values[field.id];
    if (!field.apply.requestParam || !fieldValues || fieldValues.length === 0) {
      continue;
    }
    params[field.apply.requestParam] =
      field.type === "single" ? fieldValues[0]! : fieldValues;
  }
  return params;
}

/** Static option label for a value; `undefined` when unknown (remote values
 * resolve through the consumer's `valueLabel` instead). */
export function staticValueLabel<Row>(
  field: FilterFieldDef<Row>,
  value: string,
): string | undefined {
  if (field.options.kind !== "static") return undefined;
  return field.options.options.find((option) => option.value === value)?.label;
}

// Chip ids join (fieldId, value) on a NUL byte — a character no field id or
// URL-decoded value contains — so two pairs can never collide. Built via
// fromCharCode so the source carries no literal NUL (mirrors the backend DTO).
const CHIP_ID_SEPARATOR = String.fromCharCode(0);

/** One applied chip per (field, value) pair. */
export interface AppliedFilterChip {
  /** Unique per (field, value); NUL-joined so ids cannot collide. */
  readonly id: string;
  readonly fieldId: string;
  readonly value: string;
  readonly label: string;
  readonly valueLabel: ReactNode;
}

export function appliedFilterChips<Row>(
  schema: FilterSchema<Row>,
  values: FilterValues,
  resolveValueLabel?: (fieldId: string, value: string) => ReactNode | undefined,
): AppliedFilterChip[] {
  const chips: AppliedFilterChip[] = [];
  for (const field of schema) {
    for (const value of values[field.id] ?? []) {
      chips.push({
        id: `${field.id}${CHIP_ID_SEPARATOR}${value}`,
        fieldId: field.id,
        value,
        label: field.label,
        valueLabel:
          resolveValueLabel?.(field.id, value) ??
          staticValueLabel(field, value) ??
          value,
      });
    }
  }
  return chips;
}

/* ---------------- URL codec ----------------
   Only APPLIED values serialize (drafts never touch the URL). Unrelated query
   parameters are preserved verbatim; a schema field's parameter is fully owned
   (deleted then re-appended). Pure — no router, no window — so the consumer
   decides how the params reach history (see useFilterUrlState). */

export function filterValuesToSearchParams<Row>(
  schema: FilterSchema<Row>,
  values: FilterValues,
  current: URLSearchParams,
): URLSearchParams {
  const params = new URLSearchParams(current);
  for (const field of schema) {
    params.delete(field.id);
    for (const value of values[field.id] ?? []) {
      params.append(field.id, value);
    }
  }
  return params;
}

/**
 * Decode applied values from the URL, failing SAFE: an unknown static option,
 * a value the remote source's `isValidValue` rejects, or an empty string is
 * dropped rather than surfaced as a broken filter.
 */
export function searchParamsToFilterValues<Row>(
  schema: FilterSchema<Row>,
  params: URLSearchParams,
): FilterValues {
  const raw: Record<string, readonly string[]> = {};
  for (const field of schema) {
    const candidates = params.getAll(field.id).filter((value) => {
      if (value === "") return false;
      if (field.options.kind === "static") {
        return field.options.options.some((option) => option.value === value);
      }
      return field.options.isValidValue?.(value) ?? true;
    });
    if (candidates.length > 0) raw[field.id] = candidates;
  }
  return normalizeFilterValues(schema, raw);
}
